import { Alert } from "@mui/material";
import { useCompleteRoleOnboardingMutation } from "Api/Mutations/Roles/useCompleteRoleOnboardingMutation";
import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import { useGetContextsQuery } from "Auth/Api/Queries/Context/useGetContextsQuery";
import { StepBox } from "Components/Onboarding/shared/StepBox";
import { BankConnectionList } from "Components/Shared/BankConnectionList";
import { FullHeightForm } from "Components/Shared/FormStyles";
import { useBankingAuth } from "Hooks/useBankingAuth";
import type { BankEnvironment } from "Infrastructure/Api/Api";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";

const COMPLETION_RESOURCES = Resources.Roles.AddRole.Completion;

type Props = {
  onNextStepRequested: () => void;
};

/**
 * Final add-role merchant step. The shared bank step has no submit (the legacy flow ends with the
 * OAuth callback finishing onboarding server-side); here connecting an account unlocks "complete
 * the role" directly — no separate completion screen.
 */
export const AddRoleBankAccountStep = ({
  onNextStepRequested: _unused,
}: Props) => {
  const { t } = useResource();
  const { isPending, authorize, selectedEnvironment } = useBankingAuth();

  // The list is scoped to the active role context: connecting a bank stamps (or duplicates) the
  // account rows for this context, so a non-empty list honestly means THIS role has its bank
  // connected — another context's accounts never unlock the step.
  const { data: accounts } = useBankingAccounts();
  const hasConnectedAccount = !!accounts?.items?.length;

  const { data: contextsData } = useGetContextsQuery();
  const activeIdentityUserPublicID = contextsData?.activeIdentityUserPublicID;

  const { mutate: completeRole, isPending: isCompleting } =
    useCompleteRoleOnboardingMutation({
      onSuccess: () => {
        toast.success(t(COMPLETION_RESOURCES.Success));
        // The role just flipped Active — reload so the route gate stops forcing the wizard.
        window.location.replace(appUrl("homepage"));
      },
      onError: () => {
        toast.error(t(COMPLETION_RESOURCES.Error));
      },
    });

  const onBankSelected = (environment: BankEnvironment) => {
    if (!isPending) {
      authorize({ environment });
    }
  };

  return (
    <FullHeightForm>
      <StepBox
        submitButtonLabel={t(COMPLETION_RESOURCES.Submit)}
        onSubmitClick={() =>
          activeIdentityUserPublicID && completeRole(activeIdentityUserPublicID)
        }
        submitDisabled={!hasConnectedAccount}
        isSubmitting={isCompleting}
        submitButtonDescription={
          hasConnectedAccount ? (
            <Alert severity="success">
              {t(Resources.Roles.AddRole.BankStep.Connected)}
            </Alert>
          ) : undefined
        }
      >
        <BankConnectionList
          isPending={isPending}
          onBankSelected={onBankSelected}
          selectedEnvironment={selectedEnvironment}
        />
      </StepBox>
    </FullHeightForm>
  );
};
